// カテゴリ専用ページ：素材アップロード → params → 動画設定 → 生成
import { useCallback, useEffect, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, Loader2, Sparkles, Video, X } from "lucide-react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { useCredits } from "@/hooks/useCredits";
import {
  TEMPLATE_STEP1_CREDITS,
  calculateStep2Credits,
  type TemplateDuration,
  type TemplateResolution,
} from "@/lib/templatePricing";
import { track } from "@/lib/analytics";
import { extractFrameFromVideo } from "@/lib/videoFrameExtractor";
import {
  getCategoryById,
  type Category,
} from "@/lib/transformationCategories";
import type { CategoryParams } from "@/lib/buildTransformationPrompt";
import {
  BackgroundModeSelector,
  type BackgroundMode,
  type BackgroundAsset as BgModeAsset,
} from "@/components/template/BackgroundModeSelector";

export const Route = createFileRoute("/_authenticated/create/template/category/$category")({
  head: () => ({
    meta: [
      { title: "変身カテゴリ | KAGEM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: TemplateCategoryPage,
});

const VIDEO_TYPES = ["video/mp4", "video/quicktime"];
const VIDEO_MAX = 100 * 1024 * 1024;
const VIDEO_MAX_DURATION = 15;

type SelfieAsset = {
  videoPath: string;
  videoSignedUrl: string;
  framePath: string;
  frameSignedUrl: string;
  name: string;
  size: number;
  duration: number;
};

type BackgroundAsset = {
  path: string;
  signedUrl: string;
  name: string;
  size: number;
};

const SESSION_KEY = "create:template:v2";

type Persisted = {
  resolution: TemplateResolution;
  duration: TemplateDuration;
  audio: boolean;
  categoryId: string | null;
  params: CategoryParams;
  backgroundMode: BackgroundMode | null;
};

function readPersisted(): Persisted {
  if (typeof window === "undefined") return defaultPersisted();
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return defaultPersisted();
    return { ...defaultPersisted(), ...JSON.parse(raw) };
  } catch {
    return defaultPersisted();
  }
}
function defaultPersisted(): Persisted {
  return { resolution: "1080p", duration: 5, audio: false, categoryId: null, params: {}, backgroundMode: null };
}
function writePersisted(s: Persisted) {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(s));
}

function fmtBytes(b: number) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function TemplateCategoryPage() {
  const { category: categoryId } = useParams({
    from: "/_authenticated/create/template/category/$category",
  });
  const navigate = useNavigate();
  const category = getCategoryById(categoryId);

  const [userId, setUserId] = useState<string | null>(null);
  const [selfie, setSelfie] = useState<SelfieAsset | undefined>();
  const [background, setBackground] = useState<BackgroundAsset | undefined>();
  const [persisted, setPersisted] = useState<Persisted>(defaultPersisted());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [agreedRights, setAgreedRights] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const balance = useCredits(userId);

  useEffect(() => {
    if (!category) {
      toast.error("カテゴリが見つかりませんでした");
      navigate({ to: "/create/template", replace: true });
      return;
    }
    // Load persisted; if categoryId differs from URL, reset params
    const p = readPersisted();
    let next: Persisted = p;
    if (p.categoryId !== categoryId) {
      next = { ...p, categoryId, params: {} };
    }
    // カテゴリ別のデフォルト値を確定（未設定なら埋める）
    if (category.paramType === "binary_age") {
      const params = { ...next.params };
      if (!params.aging_direction) params.aging_direction = "older";
      if (typeof params.target_age !== "number") params.target_age = 60;
      next = { ...next, params };
    }
    writePersisted(next);
    setPersisted(next);
    void supabase.auth.getUser().then(({ data }) => setUserId(data.user?.id ?? null));
  }, [category, categoryId, navigate]);

  const updatePersisted = useCallback((next: Partial<Persisted>) => {
    setPersisted((prev) => {
      const merged = { ...prev, ...next };
      writePersisted(merged);
      return merged;
    });
  }, []);

  const updateParams = useCallback(
    (next: Partial<CategoryParams>) => {
      updatePersisted({ params: { ...persisted.params, ...next } });
    },
    [persisted.params, updatePersisted],
  );

  const step2Credits = calculateStep2Credits(persisted.resolution, persisted.duration, persisted.audio);
  const totalEstimate = TEMPLATE_STEP1_CREDITS + step2Credits;

  const paramsValid = (() => {
    if (!category) return false;
    const p = persisted.params;
    if (category.paramType === "binary_age") {
      return !!p.aging_direction && typeof p.target_age === "number";
    }
    if (category.paramType === "custom_text") {
      return !!p.custom_text && p.custom_text.trim().length > 0;
    }
    if (!p.selection_value || !p.selection_label) return false;
    // chips の選択肢が sub_options を持つ場合、サブ選択が必須
    const selectedOption = category.params?.options?.find((o) => o.value === p.selection_value);
    if (selectedOption?.sub_options && selectedOption.sub_options.length > 0) {
      return !!p.sub_selection_value && !!p.sub_selection_label;
    }
    return true;
  })();

  const bgMode = persisted.backgroundMode;
  const bgValid = !!bgMode && (bgMode === "auto" || bgMode === "preserve" || !!background);
  const canSubmit = !!userId && !!selfie && !!category && paramsValid && bgValid && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit || !selfie || !category) return;
    if (balance !== null && balance < totalEstimate) {
      toast.error(`動画生成には ${totalEstimate}cr 必要です（残高 ${balance}cr）`);
      return;
    }
    setSubmitting(true);
    try {
      track("template_step1_start", {
        resolution: persisted.resolution,
        duration: persisted.duration,
        audio: persisted.audio,
        has_background: !!background,
        category_id: category.id,
        category_params_summary:
          category.id === "age"
            ? `age:${persisted.params.aging_direction}:${persisted.params.target_age}`
            : category.id === "custom"
              ? "custom"
              : `${category.id}:${persisted.params.selection_value}`,
      });
      const { data, error } = await supabase.functions.invoke("create-template-step1", {
        body: {
          selfie_video_url: selfie.videoSignedUrl,
          selfie_frame_url: selfie.frameSignedUrl,
          background_mode: bgMode,
          background_url: bgMode === "auto" ? null : (background?.signedUrl ?? null),
          category_id: category.id,
          category_params: persisted.params,
          video_resolution: persisted.resolution,
          video_duration: persisted.duration,
          video_with_audio: persisted.audio,
          aspect_ratio: "9:16",
        },
      });
      if (error) {
        let body: any = null;
        try { body = await (error as any).context?.json?.(); } catch { /* ignore */ }
        if (body?.error === "CONTENT_BLOCKED") {
          toast.error("不適切なコンテンツが検出されたため生成できません。別の素材でお試しください。");
          setSubmitting(false);
          setConfirmOpen(false);
          return;
        }
        if (body?.error === "MODERATION_UNAVAILABLE") {
          toast.error("コンテンツ確認サービスが一時停止中です。時間を置いてお試しください。");
          setSubmitting(false);
          setConfirmOpen(false);
          return;
        }
        throw new Error(body?.error ? `${body.error}: ${body.detail ?? ""}` : (error as Error).message);
      }
      const genId = (data as { generation_id?: string })?.generation_id;
      if (!genId) throw new Error("生成IDが取得できませんでした");
      navigate({ to: "/create/template/$id/step1-processing", params: { id: genId } });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "送信に失敗しました";
      toast.error(msg);
      setSubmitting(false);
      setConfirmOpen(false);
    }
  };


  if (!category) {
    return null;
  }

  return (
    <main className="min-h-screen bg-background px-4 py-8 sm:py-10">
      <div className="mx-auto max-w-4xl">
        <Link
          to="/create/template"
          className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          テンプレ一覧に戻る
        </Link>

        <header className="mb-8 text-center">
          <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-3 py-1 text-xs font-semibold text-primary">
            <Sparkles className="h-3.5 w-3.5" />
            変身テンプレ
          </div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            <span className="mr-2">{category.emoji}</span>
            {category.label}
          </h1>
          {category.description && (
            <p className="mt-2 text-sm text-muted-foreground">{category.description}</p>
          )}
        </header>

        {/* Step A: 素材 */}
        <section className="mb-6">
          <h2 className="mb-3 text-sm font-bold text-muted-foreground">① 自撮り動画</h2>
          <SelfieUploader userId={userId} value={selfie} onChange={setSelfie} />
        </section>

        {/* Step A-2: 背景モード */}
        <section className="mb-6">
          <BackgroundModeSelector
            userId={userId}
            mode={persisted.backgroundMode}
            onModeChange={(m) => updatePersisted({ backgroundMode: m })}
            asset={background as BgModeAsset | undefined}
            onAssetChange={(a) => setBackground(a)}
            slotKey="cat-bg"
          />
        </section>

        {/* Step B: パラメータ */}
        <section className="mb-6">
          <h2 className="mb-3 text-sm font-bold text-muted-foreground">② 変身内容</h2>
          <Card className="p-4">
            <CategoryParamEditor
              category={category}
              params={persisted.params}
              onChange={updateParams}
            />
          </Card>
        </section>

        {/* Step C: 動画設定 */}
        <section className="mb-6">
          <h2 className="mb-3 text-sm font-bold text-muted-foreground">③ 動画の設定</h2>
          <Card className="p-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <SettingToggle
                label="解像度"
                value={persisted.resolution}
                onChange={(v) => updatePersisted({ resolution: v })}
                options={[
                  { v: "720p" as const, label: "720p" },
                  { v: "1080p" as const, label: "1080p" },
                ]}
              />
              <SettingToggle
                label="長さ"
                value={persisted.duration}
                onChange={(v) => updatePersisted({ duration: v })}
                options={[
                  { v: 5 as const, label: "5秒" },
                  { v: 10 as const, label: "10秒" },
                  { v: 15 as const, label: "15秒" },
                ]}
              />
              <SettingToggle
                label="音声"
                value={persisted.audio}
                onChange={(v) => updatePersisted({ audio: v })}
                options={[
                  { v: false, label: "なし" },
                  { v: true, label: "あり (+50%)" },
                ]}
              />
            </div>
          </Card>
        </section>

        <Card className="mb-6 p-5">
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
            <div>
              <p className="font-semibold">消費クレジット</p>
              <p className="mt-1 font-bold text-foreground">合計 約 {totalEstimate}cr</p>
            </div>
            <div className="text-right text-xs text-muted-foreground">
              残高: <span className="font-bold text-foreground">{balance ?? "—"}cr</span>
            </div>
          </div>
        </Card>

        <div className="sticky bottom-4 flex justify-center">
          <Button
            size="lg"
            disabled={!canSubmit}
            onClick={() => setConfirmOpen(true)}
            className="min-w-[280px] shadow-lg"
          >
            <Sparkles className="mr-1 h-4 w-4" />
            動画を生成する（約 {totalEstimate}cr）
          </Button>
        </div>
      </div>

      <Dialog
        open={confirmOpen}
        onOpenChange={(o) => {
          setConfirmOpen(o);
          if (!o) setAgreedRights(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>動画生成を開始しますか？</DialogTitle>
            <DialogDescription>
              合計 <strong>{totalEstimate}クレジット</strong> を消費して動画を生成します。完了まで数分かかります。
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-2 rounded-md border border-border p-3 text-sm leading-relaxed">
            <Checkbox
              checked={agreedRights}
              onCheckedChange={(v) => setAgreedRights(v === true)}
              className="mt-0.5"
              disabled={submitting}
            />
            <span>
              アップロードする画像・動画は、自分自身または同意を得た人物のものであり、第三者の権利（肖像権・著作権・プライバシー権等）を侵害しないことを確認しました。
            </span>
          </label>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} disabled={submitting}>
              キャンセル
            </Button>
            <Button onClick={handleSubmit} disabled={submitting || !agreedRights}>
              {submitting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              開始する
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}

/* ---------- Category param editor ---------- */
function CategoryParamEditor({
  category,
  params,
  onChange,
}: {
  category: Category;
  params: CategoryParams;
  onChange: (next: Partial<CategoryParams>) => void;
}) {
  if (category.paramType === "binary_age") {
    const direction = params.aging_direction ?? "older";
    const range =
      direction === "younger"
        ? (category.params?.ageRangeYounger ?? { min: 5, max: 20 })
        : (category.params?.ageRangeOlder ?? { min: 40, max: 90 });
    const defaultAge = direction === "younger" ? 10 : 60;
    const age = params.target_age ?? defaultAge;
    return (
      <div className="space-y-3">
        <div className="flex gap-2">
          {(["older", "younger"] as const).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => onChange({ aging_direction: d, target_age: d === "younger" ? 10 : 60 })}
              className={cn(
                "flex-1 rounded-md border px-3 py-2 text-sm font-semibold transition",
                direction === d
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-input bg-background text-muted-foreground hover:text-foreground",
              )}
            >
              {d === "older" ? "老化させる" : "若返らせる"}
            </button>
          ))}
        </div>
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">
            目標年齢：<span className="font-bold text-foreground">{age}歳</span>
          </label>
          <input
            type="range"
            min={range.min}
            max={range.max}
            value={age}
            onChange={(e) => onChange({ target_age: Number(e.target.value) })}
            className="w-full"
          />
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span>{range.min}歳</span>
            <span>{range.max}歳</span>
          </div>
        </div>
      </div>
    );
  }

  if (category.paramType === "selection") {
    const options = category.params?.options ?? [];
    return (
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {options.map((o) => {
          const selected = params.selection_value === o.value;
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => onChange({ selection_value: o.value, selection_label: o.label })}
              className={cn(
                "rounded-md border px-3 py-2 text-sm font-semibold transition",
                selected
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-input bg-background text-muted-foreground hover:text-foreground",
              )}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    );
  }

  if (category.paramType === "chips") {
    const options = category.params?.options ?? [];
    const selectedOption = options.find((o) => o.value === params.selection_value);
    const subOptions = selectedOption?.sub_options;
    return (
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {options.map((o) => {
            const selected = params.selection_value === o.value;
            return (
              <button
                key={o.value}
                type="button"
                aria-label={o.label}
                onClick={() =>
                  onChange({
                    selection_value: o.value,
                    selection_label: o.label,
                    // メイン chip 切替時はサブ選択を必ずクリア
                    sub_selection_value: undefined,
                    sub_selection_label: undefined,
                  })
                }
                className={cn(
                  "rounded-full border px-3 py-1.5 text-xs font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                  selected
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-input bg-background text-muted-foreground hover:text-foreground",
                )}
              >
                {o.label}
              </button>
            );
          })}
        </div>
        {subOptions && subOptions.length > 0 && (
          <div className="rounded-md border border-muted bg-muted/40 p-3">
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              → 詳細
            </p>
            <div className="flex flex-wrap gap-1.5">
              {subOptions.map((s) => {
                const selected = params.sub_selection_value === s.value;
                return (
                  <button
                    key={s.value}
                    type="button"
                    aria-label={s.label}
                    onClick={() =>
                      onChange({
                        sub_selection_value: s.value,
                        sub_selection_label: s.label,
                      })
                    }
                    className={cn(
                      "rounded-full border px-2.5 py-1 text-xs transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                      selected
                        ? "border-primary bg-primary text-primary-foreground font-semibold"
                        : "border-muted bg-background/60 text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold text-muted-foreground">
        変身内容を自由に記述
      </label>
      <Textarea
        rows={3}
        maxLength={300}
        value={params.custom_text ?? ""}
        onChange={(e) => onChange({ custom_text: e.target.value })}
        placeholder="例：戦国時代の武士、宇宙飛行士、カウボーイ…"
      />
      <p className="mt-1 text-right text-xs text-muted-foreground">
        {(params.custom_text ?? "").length} / 300
      </p>
    </div>
  );
}

/* ---------- Setting toggle ---------- */
function SettingToggle<T extends string | number | boolean>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: { v: T; label: string }[];
}) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold text-muted-foreground">{label}</label>
      <div className="inline-flex w-full rounded-md border border-input p-0.5">
        {options.map((o) => (
          <button
            key={String(o.v)}
            type="button"
            onClick={() => onChange(o.v)}
            className={cn(
              "flex-1 rounded px-3 py-1.5 text-sm font-semibold transition",
              value === o.v
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---------- Selfie uploader (video only) ---------- */
function SelfieUploader({
  userId,
  value,
  onChange,
}: {
  userId: string | null;
  value: SelfieAsset | undefined;
  onChange: (a: SelfieAsset | undefined) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File) => {
    if (!userId) {
      toast.error("ログインが必要です");
      return;
    }
    if (!VIDEO_TYPES.includes(file.type)) {
      toast.error("動画のみ対応しています (mp4 / mov)");
      return;
    }
    if (file.size > VIDEO_MAX) {
      toast.error(`動画サイズが大きすぎます（最大 ${fmtBytes(VIDEO_MAX)}）`);
      return;
    }
    setUploading(true);
    try {
      const frame = await extractFrameFromVideo(file);
      if (frame.duration > VIDEO_MAX_DURATION + 0.5) {
        toast.error(`${VIDEO_MAX_DURATION}秒以内の動画をご利用ください`);
        setUploading(false);
        return;
      }

      const ts = Date.now();
      const ext = file.name.split(".").pop() ?? "mp4";
      const videoPath = `${userId}/${ts}-selfie.${ext}`;
      const framePath = `${userId}/${ts}-selfie-frame.jpg`;

      const [videoUp, frameUp] = await Promise.all([
        supabase.storage.from("uploads").upload(videoPath, file, {
          contentType: file.type,
          upsert: false,
        }),
        supabase.storage.from("uploads").upload(framePath, frame.blob, {
          contentType: "image/jpeg",
          upsert: false,
        }),
      ]);
      if (videoUp.error) throw videoUp.error;
      if (frameUp.error) throw frameUp.error;

      const [{ data: vsigned }, { data: fsigned }] = await Promise.all([
        supabase.storage.from("uploads").createSignedUrl(videoPath, 60 * 60 * 24),
        supabase.storage.from("uploads").createSignedUrl(framePath, 60 * 60 * 24),
      ]);

      onChange({
        videoPath,
        videoSignedUrl: vsigned?.signedUrl ?? "",
        framePath,
        frameSignedUrl: fsigned?.signedUrl ?? "",
        name: file.name,
        size: file.size,
        duration: frame.duration,
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "アップロードに失敗しました");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card className="overflow-hidden">
      <div className="border-b border-border bg-muted/30 px-4 py-2">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Video className="h-5 w-5 text-primary" />
          自撮り動画 <span className="text-destructive">*</span>
        </div>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          mp4 / mov ・ 15秒以内 ・ 最大 100 MB
        </p>
      </div>
      <div className="p-3">
        {value ? (
          <div className="space-y-2">
            <div className="relative aspect-square overflow-hidden rounded-md bg-black">
              <video
                src={value.videoSignedUrl}
                className="h-full w-full object-contain"
                controls
                playsInline
                muted
              />
              <button
                type="button"
                onClick={() => onChange(undefined)}
                className="absolute right-1 top-1 rounded-full bg-background/80 p-1 hover:bg-background"
                aria-label="削除"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="truncate text-[11px] text-muted-foreground">
              {value.name} ・ {fmtBytes(value.size)} ・ {value.duration.toFixed(1)}秒
            </p>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex aspect-square w-full flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-input bg-muted/30 text-xs text-muted-foreground hover:bg-muted/50"
            disabled={uploading}
          >
            {uploading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                解析中...
              </>
            ) : (
              <>
                <Video className="h-6 w-6" />
                動画を選択
              </>
            )}
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={VIDEO_TYPES.join(",")}
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleFile(f);
            e.target.value = "";
          }}
        />
      </div>
    </Card>
  );
}

